import React, { useMemo, useState } from "react";
import "./FilterMenu.css";

/**
 * Column filter dropdown.
 *
 * NOTE on class names: every class here is prefixed `sev-filter-…`
 * (SimpleExcelViewer filter) so they can never collide with chart/axis
 * or any other global styles. Do not reuse these names elsewhere.
 */
function FilterMenu({ colIndex, rows, currentFilter, onApply, onClose }) {
  const [searchTerm, setSearchTerm] = useState("");

  const allValues = useMemo(() => {
    const vals = new Set();
    // Skip row 0 (header row) — it should not appear as a filterable value
    rows.forEach((row, idx) => {
      if (idx === 0) return;
      const v = row[colIndex];
      vals.add(v == null || v === "" ? "(Blank)" : String(v));
    });
    const arr = Array.from(vals);
    // Sort numerically for number values, then alphabetically for text
    arr.sort((a, b) => {
      const aNum = Number(a);
      const bNum = Number(b);
      const aIsNum = a !== "(Blank)" && a !== "" && !isNaN(aNum);
      const bIsNum = b !== "(Blank)" && b !== "" && !isNaN(bNum);
      if (aIsNum && bIsNum) return aNum - bNum;
      if (aIsNum) return -1;
      if (bIsNum) return 1;
      return a.localeCompare(b);
    });
    return arr;
  }, [colIndex, rows]);

  const [selected, setSelected] = useState(() => {
    if (currentFilter) return new Set(currentFilter);
    return new Set(allValues);
  });

  const filteredValues = useMemo(() => {
    if (!searchTerm) return allValues;
    const lower = searchTerm.toLowerCase();
    return allValues.filter(v => v.toLowerCase().includes(lower));
  }, [allValues, searchTerm]);

  const isAllFilteredSelected = filteredValues.every(v => selected.has(v));

  const toggleAll = () => {
    if (isAllFilteredSelected) {
      const next = new Set(selected);
      filteredValues.forEach(v => next.delete(v));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filteredValues.forEach(v => next.add(v));
      setSelected(next);
    }
  };

  const toggleValue = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setSelected(next);
  };

  return (
    <div className="sev-filter-popup" onClick={e => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}>
      <div className="sev-filter-search-container">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="sev-filter-search-input"
          autoFocus
        />
      </div>
      <div className="sev-filter-popup-list">
        {filteredValues.length > 0 && (
          <label className="sev-filter-popup-item sev-filter-select-all">
            <input
              type="checkbox"
              checked={isAllFilteredSelected}
              onChange={toggleAll}
            />
            <span>(Select All Search Results)</span>
          </label>
        )}
        {filteredValues.map(val => (
          <label key={val} className="sev-filter-popup-item">
            <input type="checkbox" checked={selected.has(val)} onChange={() => toggleValue(val)} />
            <span className="sev-filter-item-text">{val}</span>
          </label>
        ))}
        {filteredValues.length === 0 && (
          <div className="sev-filter-no-matches">No matches found</div>
        )}
      </div>
      <div className="sev-filter-popup-footer">
        <button
          type="button"
          className="sev-filter-btn-ok"
          disabled={selected.size === 0}
          aria-disabled={selected.size === 0}
          onClick={() => {
            onApply(selected.size === allValues.length ? null : selected);
            onClose();
          }}
        >
          OK
        </button>
        <button type="button" className="sev-filter-btn-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

export default FilterMenu;
